import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

export type FieldKey = string
export type FieldError = { key: FieldKey; message: string }

type ErrorBus = {
  /** all current errors by field key */
  errors: Record<FieldKey, string>
  /** true if any field has an error */
  hasErrors: boolean
  /** register an input (optional; used to track DOM nodes for scrolling) */
  register: (key: FieldKey, el?: HTMLElement | null) => () => void
  /** set/replace error for a field */
  setError: (key: FieldKey, message: string) => void
  /** clear error for a field */
  clearError: (key: FieldKey) => void
  /** clear all errors (e.g., when switching pages) */
  clearAll: () => void
  /** focus/scroll to the first error if any */
  focusFirstError: () => void
}

const ErrorBusContext = createContext<ErrorBus | null>(null)

export const FormErrorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [errors, setErrors] = useState<Record<FieldKey, string>>({})
  const inputs = useRef<Map<FieldKey, HTMLElement | null>>(new Map())

  const register = useCallback((key: FieldKey, el?: HTMLElement | null) => {
    if (el) inputs.current.set(key, el)
    return () => inputs.current.delete(key)
  }, [])

  const setError = useCallback((key: FieldKey, message: string) => {
    setErrors((prev) => ({ ...prev, [key]: message }))
  }, [])

  const clearError = useCallback((key: FieldKey) => {
    setErrors((prev) => {
      if (!(key in prev)) return prev

      const { [key]: _, ...rest } = prev
      return rest
    })
  }, [])

  const clearAll = useCallback(() => setErrors({}), [])

  const focusFirstError = useCallback(() => {
    const firstKey = Object.keys(errors)[0]
    if (!firstKey) return
    const el = inputs.current.get(firstKey)
    el?.focus?.()
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
  }, [errors])

  const value = useMemo<ErrorBus>(
    () => ({
      errors,
      hasErrors: Object.keys(errors).length > 0,
      register,
      setError,
      clearError,
      clearAll,
      focusFirstError,
    }),
    [errors, register, setError, clearError, clearAll, focusFirstError]
  )

  return <ErrorBusContext.Provider value={value}>{children}</ErrorBusContext.Provider>
}

export function useFormErrors() {
  const ctx = useContext(ErrorBusContext)
  if (!ctx) throw new Error('useFormErrors must be used within FormErrorProvider')
  return ctx
}

/** Convenience hook for a single field */
export function useFieldError(key: FieldKey) {
  const { errors, setError, clearError, register } = useFormErrors()
  return {
    message: errors[key],
    hasError: key in errors,
    setError: (msg: string) => setError(key, msg),
    clearError: () => clearError(key),
    register: (el?: HTMLElement | null) => register(key, el),
  }
}
